import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type {
  CompletedCalculationTactic,
  LoadFlowScenarioResults
} from '../../../..';
import {
  AttentionStatusStorage,
  type ForStoringAttentionStatus
} from '../../../../evaluation/AttentionStatusRepository';
import type { RulesProvider } from '../../../../evaluation/RulesProvider';
import { createAcLinesLoadFlowDetails } from '../elementDetais/acLinesLoadFlowDetails';
import { createEnergyConsumersLoadFlowDetails } from '../elementDetais/energyConsumersLoadFlowDetails';
import { createExternalNetworkInjectionLoadFlowDetails } from '../elementDetais/externalNetworkInjectionLoadFlowDetails';
import {
  createLvBusbarsLoadFlowDetails,
  createMvBusbarsLoadFlowDetails
} from '../elementDetais/mvBusbarsLoadFlowDetails';
import { createPowerTransformersLoadFlowDetails } from '../elementDetais/powerTransformersLoadFlowDetails';
import type {
  AcLineLoadFlowDetails,
  BusbarLoadFlowDetails,
  EnergyConsumerLoadFlowDetails,
  ExternalNetworkInjectionLoadFlowDetails,
  GridElementsLoadFlowDetails,
  PowerTransformerLoadFlowDetails
} from '../gridElementLoadFlowDetails.types';
import { LowVoltageEvaluationEngineFactory } from '../lowVoltage/LowVoltageEvaluationEngineFactory';
import { MediumVoltageEvaluationEngineFactory } from './MediumVoltageEvaluationEngineFactory';
import { Features } from '../../../../createGridElementCalculationSetExecutionDetails';

export function createMvLoadFlowDetails(
  features: Features,
  scenariosResults: LoadFlowScenarioResults[],
  rulesProvider: RulesProvider,
  calculationTactic: CompletedCalculationTactic,
  attentionStatusRepository: ForStoringAttentionStatus
): GridElementsLoadFlowDetails {
  const anyScenarioResults = scenariosResults.flatMap(
    scenarioResults => scenarioResults.results
  );

  const anyScenarioInputs = scenariosResults.flatMap(
    scenarioResults => scenarioResults.inputs
  );

  if (anyScenarioResults.length === 0) {
    return [];
  }

  const mediumVoltageEnginesFactory = new MediumVoltageEvaluationEngineFactory(
    rulesProvider
  );

  const lowVoltageEnginesFactory = new LowVoltageEvaluationEngineFactory(
    rulesProvider
  );

  const energyConsumersDetails: EnergyConsumerLoadFlowDetails[] =
    createEnergyConsumersLoadFlowDetails(
      features,
      mediumVoltageEnginesFactory.createEnergyConsumerEvaluationEngine(),
      anyScenarioResults,
      calculationTactic,
      attentionStatusRepository,
      scenariosResults[0].inputs
    );

  const acLinesDetails: AcLineLoadFlowDetails[] = createAcLinesLoadFlowDetails(
    features,
    mediumVoltageEnginesFactory.createAcLineSegmentEvaluationEngine(),
    anyScenarioResults,
    calculationTactic.calculationSetResult.grid.functional,
    attentionStatusRepository
  );
  const functionalGrid = calculationTactic.calculationSetResult.grid.functional;

  const powerTransformersDetails: PowerTransformerLoadFlowDetails[] =
    createPowerTransformersLoadFlowDetails(
      features,
      mediumVoltageEnginesFactory.createPowerTransformerEvaluationEngine(),
      anyScenarioResults,
      functionalGrid,
      anyScenarioInputs[0],
      attentionStatusRepository
    );

  const mvBusbarsDetails: BusbarLoadFlowDetails[] =
    createMvBusbarsLoadFlowDetails(
      features,
      mediumVoltageEnginesFactory,
      anyScenarioResults,
      functionalGrid,
      attentionStatusRepository
    );

  const lvBusbarsDetails: BusbarLoadFlowDetails[] =
    createLvBusbarsLoadFlowDetails(
      features,
      lowVoltageEnginesFactory,
      anyScenarioResults,
      calculationTactic,
      calculationTactic.calculationSetResult.grid.functional,
      AttentionStatusStorage.noStorage(),
      scenariosResults[0].inputs
    );

  const externalNetworkInjectionDetails: ExternalNetworkInjectionLoadFlowDetails[] =
    createExternalNetworkInjectionLoadFlowDetails(
      features,
      anyScenarioInputs[0]
    );

  return [
    ...energyConsumersDetails,
    ...acLinesDetails,
    ...powerTransformersDetails,
    ...mvBusbarsDetails,
    ...lvBusbarsDetails,
    ...externalNetworkInjectionDetails
  ];
}
